import styled from 'styled-components';
import { compareObjects } from '../../../functions/utils/comapareObjects';
import { launcherStatesAllFalse } from '../../../hooks/stores/launcherStatesStore';
import { UIData, useUIStore } from '../../../hooks/stores/uiStore';
import { stateViewsInfo } from '../../../info/stateViewsInfo';
import { VisibilityButton } from './VisibilityButton';
import { useScene } from '../../../context/sceneContext';

export const StateFlagItem = ({
  stateView,
  columnIndex,
}: {
  stateView: keyof typeof stateViewsInfo;
  columnIndex?: number;
}) => {
  const stateViewData = stateViewsInfo[stateView];
  const scene = useScene();
  const [UIData, setUIData] = useUIStore<UIData>();
  const useDirectVisibility = UIData.useDirectVisibility;

  const isActive = scene.isViewActive(stateView);

  const isCurrentStateDifferentFromDisplayed =
    !compareObjects(UIData.currentLauncherState, stateViewData.significantLauncherState) &&
    UIData.selectedElementIDs.length > 0;

  const computedVisibility = scene.getSelectedElementsComputedVisibility(stateViewData.significantLauncherState, {
    ...launcherStatesAllFalse,
    ...stateViewData.launcherStates,
  });
  const directVisibility = scene.getSelectedElementsDirectVisibility(stateViewData.significantLauncherState);

  const setHoveredLabel = (label?: string) => {
    if (!columnIndex) return;

    const newColumnLabelsHovered = [];
    newColumnLabelsHovered[columnIndex] = label;
    setUIData({
      stateViewControllerColumnLabelsHovered: newColumnLabelsHovered,
    });
  };

  const setActive = () => {
    scene.setLauncherView(stateView);
  };

  return (
    <Container
      $isLeftContainerVisible={
        computedVisibility === false || 
        (directVisibility !== undefined && !useDirectVisibility)
      }
      onMouseEnter={() => setHoveredLabel(stateViewData.label)}
      onMouseLeave={() => setHoveredLabel(undefined)}
    >
      <SubButtonsLeftContainer className="subButtonsLeft">
        <VisibilityButton
          forState={stateViewData.significantLauncherState}
          launcherStates={{
            ...launcherStatesAllFalse,
            ...stateViewData.launcherStates,
          }}
        />
      </SubButtonsLeftContainer>

      <MainButton
        onClick={setActive}
        $isActive={isActive}
        $isCurrentStateDifferentFromDisplayed={isCurrentStateDifferentFromDisplayed}
        $computedVisibility={computedVisibility}
        $useDirectVisibility={useDirectVisibility}
      >
        <i className={stateViewData.iconClassName} style={{ fontSize: '24px', lineHeight: '24px' }} />
      </MainButton>
    </Container>
  );
};

const Container = styled.div<{ $isLeftContainerVisible?: boolean }>`
  position: relative;

  box-sizing: border-box;
  width: 50px;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;

  .subButtonsLeft {
    transition: opacity var(--transition-2);
    opacity: 0;
  }

  &:hover {
    .subButtonsLeft {
      opacity: 1;
    }
  }

  ${({ $isLeftContainerVisible }) =>
    $isLeftContainerVisible &&
    `
      .subButtonsLeft {
        opacity: 1;
      }
    `}
`;

const SubButtonsLeftContainer = styled.div`
  height: 100%;
  position: absolute;
  left: 3px;
  translate: -100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const MainButton = styled.div<{
  $isActive?: boolean;
  $isCurrentStateDifferentFromDisplayed?: boolean;
  $computedVisibility?: boolean;
  $useDirectVisibility?: boolean;
}>`
  --text-color: var(--main-2);
  --text-color-hovered: var(--main-1);
  --border-color: var(--neutral-3);

  ${({ $isActive, $isCurrentStateDifferentFromDisplayed }) =>
    $isActive &&
    `
      --border-color: var(${$isCurrentStateDifferentFromDisplayed ? '--primary-2' : '--primary-1'});
      --text-color: var(${$isCurrentStateDifferentFromDisplayed ? '--primary-2' : '--primary-1'});
      --text-color-hovered: var(--primary-1);
    `}

  ${({ $computedVisibility, $useDirectVisibility }) =>
    $computedVisibility === false &&
    `
    --text-color: var(--error-1);
    --text-color-hovered: var(--error-1);
  `}

  cursor: pointer;
  width: 40px;
  height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;

  color: var(--text-color);
  background-color: var(--neutral-2);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-1);

  transition:
    opacity var(--transition-2),
    background-color var(--transition-2),
    color var(--transition-2),
    border var(--transition-2);
  &:hover {
    background-color: var(--neutral-3);
    color: var(--text-color-hovered);
  }
`;
